import axios from 'axios';
import { ADMIN_API_BASE } from './api_admin';

/**
 * Клиент админской части рекламного модуля.
 * База наследуется из api_admin.js: `${API_URL}/api/admin-panel`.
 */
export const ADMIN_ADS_BASE = `${ADMIN_API_BASE}/ads`;

const _err = (error) => {
    if (error.response) {
        console.error('Admin ads API error:', error.response.status, error.response.data);
    } else {
        console.error('Admin ads API error:', error.message);
    }
};

const auth = (token) => ({
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
});

export const adminAdsError = (e, fallback = 'Не удалось выполнить операцию') => {
    const data = e?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (data.detail) return data.detail;
    const field = Object.keys(data)[0];
    if (field) {
        const value = data[field];
        return Array.isArray(value) ? `${field}: ${value[0]}` : String(value);
    }
    return fallback;
};

const qs = (params) => {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') search.append(key, value);
    });
    return search.toString();
};


// ===================== МОДЕРАЦИЯ =====================

export const APIAdminAdsQueue = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/moderation/queue/?${qs(params)}`, auth(token));
        return r.data; // { success, items, total, page, pages }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsPendingCreatives = async (token, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/moderation/creatives/?${qs(params)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsModerationLog = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/moderation/log/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignApprove = async (token, campaignId, comment = '') => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/campaigns/${campaignId}/approve/`, { comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignReject = async (token, campaignId, { reason, comment }) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/campaigns/${campaignId}/reject/`, { reason, comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignRequestChanges = async (token, campaignId, { reason, comment }) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/campaigns/${campaignId}/request-changes/`,
            { reason, comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCreativeApprove = async (token, creativeId, comment = '') => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/creatives/${creativeId}/approve/`, { comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCreativeReject = async (token, creativeId, { reason, comment }) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/creatives/${creativeId}/reject/`, { reason, comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignForcePause = async (token, campaignId, comment = '') => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/campaigns/${campaignId}/force-pause/`, { comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignForceArchive = async (token, campaignId, comment = '') => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/campaigns/${campaignId}/force-archive/`, { comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== КАМПАНИИ =====================

export const APIAdminAdsCampaigns = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/campaigns/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsCampaignDetail = async (token, campaignId) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/campaigns/${campaignId}/`, auth(token));
        return r.data; // { success, campaign, advertiser, moderation_log }
    } catch (e) { _err(e); throw e; }
};


// ===================== АККАУНТЫ =====================

export const APIAdminAdsAccounts = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/accounts/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAccountDetail = async (token, accountId) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/accounts/${accountId}/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAccountSuspend = async (token, accountId, reason) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/accounts/${accountId}/suspend/`, { reason }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAccountActivate = async (token, accountId) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/accounts/${accountId}/activate/`, {}, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAccountSetTrusted = async (token, accountId, isTrusted) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/accounts/${accountId}/set-trusted/`,
            { is_trusted: isTrusted }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAccountAdjustBalance = async (token, accountId, { amount, comment }) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/accounts/${accountId}/adjust-balance/`,
            { amount, comment }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ПЛОЩАДКИ =====================

export const APIAdminAdsPlacements = async (token) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/placements/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsPlacementCreate = async (token, data) => {
    try {
        const r = await axios.post(`${ADMIN_ADS_BASE}/placements/create/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsPlacementUpdate = async (token, placementId, data) => {
    try {
        const r = await axios.patch(
            `${ADMIN_ADS_BASE}/placements/${placementId}/update/`, data, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsPlacementDelete = async (token, placementId) => {
    try {
        const r = await axios.delete(
            `${ADMIN_ADS_BASE}/placements/${placementId}/delete/`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ОТЧЁТЫ =====================

export const APIAdminAdsReportOverview = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/reports/overview/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsReportAdvertisers = async (token, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/reports/advertisers/?${qs(params)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsReportPlacements = async (token, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/reports/placements/?${qs(params)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsReportModeration = async (token, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/reports/moderation/?${qs(params)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsReportAccount = async (token, accountId, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/reports/accounts/${accountId}/?${qs(params)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsReportExport = async (token, report, params = {}) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/reports/export/?report=${report}&${qs(params)}`,
            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRunAggregation = async (token, date = null) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/reports/run-aggregation/`,
            date ? { date } : {},
            auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ОПТИМИЗАЦИЯ =====================

export const APIAdminAdsStrategies = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/strategies/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsStrategyLogs = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/strategies/logs/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRunStrategies = async (token, campaignId = null) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/strategies/run/`,
            campaignId ? { campaign_id: campaignId } : {},
            auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRules = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/rules/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRuleLogs = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/rules/logs/?${qs(params)}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRunRules = async (token) => {
    try {
        const r = await axios.post(`${ADMIN_ADS_BASE}/rules/run/`, {}, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsRunRecommendations = async (token) => {
    try {
        const r = await axios.post(`${ADMIN_ADS_BASE}/recommendations/run/`, {}, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ДИАГНОСТИКА =====================

export const APIAdminAdsCandidates = async (token, placementCode) => {
    try {
        const r = await axios.get(
            `${ADMIN_ADS_BASE}/debug/candidates/${placementCode}/`, auth(token)
        );
        return r.data; // { success, placement, count, items }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsInvalidateCache = async (token, placement = null) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/debug/invalidate-cache/`, { placement }, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsDryRun = async (token, { placement, context }) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/debug/dry-run/`, { placement, context }, auth(token)
        );
        return r.data; // { success, winners, debug }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsFraudReport = async (token, days = 7) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/debug/fraud-report/?days=${days}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ЗАЯВКИ МЕНЕДЖЕРА =====================

export const APIAdminAdsApplications = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/applications/?${qs(params)}`, auth(token));
        return r.data; // { success, items, total, page, pages, counters }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsApplicationDetail = async (token, campaignId) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/applications/${campaignId}/`, auth(token));
        return r.data; // { success, advertiser, account_id, campaign, geo_scope, geo_scope_display, oversell }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsApplicationCreate = async (token, payload) => {
    try {
        const r = await axios.post(`${ADMIN_ADS_BASE}/applications/create/`, payload, auth(token));
        return r.data; // { success, advertiser_id, account_id, campaign, group_id, creative_ids }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsApplicationUpdate = async (token, campaignId, payload) => {
    try {
        const r = await axios.patch(
            `${ADMIN_ADS_BASE}/applications/${campaignId}/update/`, payload, auth(token)
        );
        return r.data; // { success, campaign }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsApplicationSubmit = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/applications/${campaignId}/submit/`, {}, auth(token)
        );
        return r.data; // { success, auto_approved, campaign } | 400 { error, errors: [...] }
    } catch (e) { _err(e); throw e; }
};

// Креатив с изображением — multipart. Content-Type НЕ указываем: axios сам
// проставит boundary для FormData.
export const APIAdminAdsApplicationCreativeCreate = async (token, groupId, formData) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/applications/groups/${groupId}/creatives/create/`,
            formData,
            { headers: { Authorization: `Bearer ${token}` } },
        );
        return r.data; // { success, creative }
    } catch (e) { _err(e); throw e; }
};

// Рекламодатели — для выбора существующего в заявке.
export const APIAdminAdsAdvertisers = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/advertisers/?${qs(params)}`, auth(token));
        return r.data; // { success, items, total, page, pages, counters }
    } catch (e) { _err(e); throw e; }
};

// ===================== ЭКОНОМИКА РЕКЛАМЫ (НАСТРОЙКИ) =====================

export const APIAdminAdsEconomics = async (token) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/settings/economics/`, auth(token));
        return r.data; // { success, groups: [{ title, items: [{ key, label, type, options, value, default }] }] }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsEconomicsUpdate = async (token, values) => {
    try {
        const r = await axios.put(
            `${ADMIN_ADS_BASE}/settings/economics/update/`, { values }, auth(token)
        );
        return r.data; // { success, changed }
    } catch (e) { _err(e); throw e; }
};


// ===================== РЕКЛАМОДАТЕЛИ (СУЩНОСТЬ БИЗНЕСА) =====================

export const APIAdminAdsAdvertiserDetail = async (token, advertiserId) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/advertisers/${advertiserId}/`, auth(token));
        return r.data; // { success, advertiser }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAdvertiserCreate = async (token, data) => {
    try {
        const r = await axios.post(`${ADMIN_ADS_BASE}/advertisers/create/`, data, auth(token));
        return r.data; // { success, advertiser }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAdvertiserUpdate = async (token, advertiserId, data) => {
    try {
        const r = await axios.patch(
            `${ADMIN_ADS_BASE}/advertisers/${advertiserId}/update/`, data, auth(token)
        );
        return r.data; // { success, advertiser }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAdvertiserBlock = async (token, advertiserId, reason) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/advertisers/${advertiserId}/block/`, { reason }, auth(token)
        );
        return r.data; // { success, campaigns_stopped }
    } catch (e) { _err(e); throw e; }
};

export const APIAdminAdsAdvertiserUnblock = async (token, advertiserId) => {
    try {
        const r = await axios.post(
            `${ADMIN_ADS_BASE}/advertisers/${advertiserId}/unblock/`, {}, auth(token)
        );
        return r.data; // { success, campaigns_resumed }
    } catch (e) { _err(e); throw e; }
};

// ===================== ЁМКОСТЬ ТЕРРИТОРИИ =====================

export const APIAdminAdsTerritoryCapacity = async (token, params = {}) => {
    try {
        const r = await axios.get(`${ADMIN_ADS_BASE}/inventory/capacity/?${qs(params)}`, auth(token));
        return r.data; // { success, city, region, capacity, slots }
    } catch (e) { _err(e); throw e; }
};